// Ashe Crystal Arrow VFX — ice crystal arrowhead with frost trail and ice-crack impact.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const TRAVEL_MS = 300;
const IMPACT_MS = 280;
const TOTAL = TRAVEL_MS + IMPACT_MS;

const HEAD_LEN = 18; // crystal tip length in px
const HEAD_W   = 5;  // half-width at widest point

export class AsheArrowVFX implements Animation {
  private elapsed = 0;
  private angle: number;
  private cos: number;
  private sin: number;
  // Pre-computed ice crack rays so the impact is deterministic
  private crackRays: { angle: number; length: number }[];

  constructor(
    private from: Point,
    private to: Point,
    private delayMs: number = 0,
  ) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    this.angle = Math.atan2(dy, dx);
    this.cos   = Math.cos(this.angle);
    this.sin   = Math.sin(this.angle);

    // 8 cracks at varying offsets from the arrow direction
    this.crackRays = Array.from({ length: 8 }, (_, i) => ({
      angle:  this.angle + (i / 8) * Math.PI * 2 + 0.2,
      length: 10 + (i * 11 % 13),
    }));
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, this.delayMs + TOTAL);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const active = this.elapsed - this.delayMs;
    if (active <= 0) return;

    if (active < TRAVEL_MS) {
      this.drawTravel(ctx, active / TRAVEL_MS);
    } else {
      this.drawImpact(ctx, (active - TRAVEL_MS) / IMPACT_MS);
    }
  }

  private drawTravel(ctx: CanvasRenderingContext2D, t: number): void {
    const eased = Easing.easeOutQuad(t);
    const tipX = this.from.x + (this.to.x - this.from.x) * eased;
    const tipY = this.from.y + (this.to.y - this.from.y) * eased;

    // Shaft tail — lags 20% behind the tip
    const tailT = Math.max(0, eased - 0.20);
    const tailX = this.from.x + (this.to.x - this.from.x) * tailT;
    const tailY = this.from.y + (this.to.y - this.from.y) * tailT;

    const cos = this.cos;
    const sin = this.sin;

    ctx.save();

    // ── Wide outer glow trail ──────────────────────────────────────────────
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = "#7dd3fc";
    ctx.lineWidth   = 7;
    ctx.shadowColor = "#0ea5e9";
    ctx.shadowBlur  = 18;
    ctx.lineCap     = "round";
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();

    // ── Core arrow shaft ──────────────────────────────────────────────────
    ctx.globalAlpha = 0.95;
    ctx.strokeStyle = "#e0f8ff";
    ctx.lineWidth   = 2.5;
    ctx.shadowColor = "#38bdf8";
    ctx.shadowBlur  = 12;
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();

    // ── 3 trailing frost sparkle dots ─────────────────────────────────────
    const sparkleOffsets = [0.12, 0.22, 0.34];
    for (let i = 0; i < sparkleOffsets.length; i++) {
      const st = Math.max(0, eased - sparkleOffsets[i]);
      if (st <= 0) continue;
      const sx = this.from.x + (this.to.x - this.from.x) * st;
      const sy = this.from.y + (this.to.y - this.from.y) * st;
      const alpha = (1 - i / sparkleOffsets.length) * 0.7;
      ctx.globalAlpha = alpha;
      ctx.shadowColor = "#7dd3fc";
      ctx.shadowBlur  = 10;
      this.drawIceStar(ctx, sx, sy, 3 - i * 0.7);
    }

    // ── Crystal arrowhead ─────────────────────────────────────────────────
    ctx.shadowColor = "#38bdf8";
    ctx.shadowBlur  = 18;

    // Main crystal body — elongated diamond
    ctx.globalAlpha = 1.0;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(
      tipX - cos * HEAD_LEN * 0.42 + sin * HEAD_W,
      tipY - sin * HEAD_LEN * 0.42 - cos * HEAD_W,
    );
    ctx.lineTo(
      tipX - cos * HEAD_LEN * 0.82 + sin * HEAD_W * 0.25,
      tipY - sin * HEAD_LEN * 0.82 - cos * HEAD_W * 0.25,
    );
    ctx.lineTo(tipX - cos * HEAD_LEN, tipY - sin * HEAD_LEN);
    ctx.lineTo(
      tipX - cos * HEAD_LEN * 0.82 - sin * HEAD_W * 0.25,
      tipY - sin * HEAD_LEN * 0.82 + cos * HEAD_W * 0.25,
    );
    ctx.lineTo(
      tipX - cos * HEAD_LEN * 0.42 - sin * HEAD_W,
      tipY - sin * HEAD_LEN * 0.42 + cos * HEAD_W,
    );
    ctx.closePath();
    ctx.fillStyle = "#7dd3fc";
    ctx.fill();

    // Bright facet — upper face of the crystal catches light
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(
      tipX - cos * HEAD_LEN * 0.42 + sin * HEAD_W,
      tipY - sin * HEAD_LEN * 0.42 - cos * HEAD_W,
    );
    ctx.lineTo(tipX - cos * HEAD_LEN, tipY - sin * HEAD_LEN);
    ctx.closePath();
    ctx.fillStyle = "#e0f8ff";
    ctx.fill();

    // Very tip — sharp white point
    ctx.globalAlpha = 1.0;
    ctx.shadowColor = "#ffffff";
    ctx.shadowBlur  = 14;
    ctx.beginPath();
    ctx.arc(tipX, tipY, 2, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const x   = this.to.x;
    const y   = this.to.y;
    const out = Easing.easeOutQuad(t);
    const fade = 1 - t;

    ctx.save();

    // ── Frost burst ring ──────────────────────────────────────────────────
    ctx.globalAlpha = fade * 0.8;
    ctx.strokeStyle = "#bae6fd";
    ctx.lineWidth   = 2;
    ctx.shadowColor = "#0ea5e9";
    ctx.shadowBlur  = 16;
    ctx.beginPath();
    ctx.arc(x, y, 5 + out * 22, 0, Math.PI * 2);
    ctx.stroke();

    // Second softer ring slightly behind
    ctx.globalAlpha = fade * 0.4;
    ctx.lineWidth   = 4;
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.arc(x, y, 2 + out * 14, 0, Math.PI * 2);
    ctx.stroke();

    // ── Ice crack lines radiating from impact ─────────────────────────────
    ctx.globalAlpha = fade * 0.85;
    ctx.strokeStyle = "#e0f8ff";
    ctx.lineWidth   = 1.2;
    ctx.shadowColor = "#38bdf8";
    ctx.shadowBlur  = 10;
    ctx.lineCap     = "round";

    for (const ray of this.crackRays) {
      const len = ray.length * out;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(ray.angle) * len, y + Math.sin(ray.angle) * len);
      ctx.stroke();

      // Small secondary branch off each crack
      if (out > 0.3) {
        const branchLen = len * 0.45;
        const branchAngle = ray.angle + 0.55;
        ctx.globalAlpha = fade * 0.45;
        ctx.beginPath();
        ctx.moveTo(
          x + Math.cos(ray.angle) * len * 0.55,
          y + Math.sin(ray.angle) * len * 0.55,
        );
        ctx.lineTo(
          x + Math.cos(ray.angle) * len * 0.55 + Math.cos(branchAngle) * branchLen,
          y + Math.sin(ray.angle) * len * 0.55 + Math.sin(branchAngle) * branchLen,
        );
        ctx.stroke();
        ctx.globalAlpha = fade * 0.85;
      }
    }

    // ── Central ice flash ─────────────────────────────────────────────────
    ctx.globalAlpha = fade * fade * 0.9;
    ctx.fillStyle   = "#ffffff";
    ctx.shadowColor = "#7dd3fc";
    ctx.shadowBlur  = 20;
    ctx.beginPath();
    ctx.arc(x, y, 5 * (1 - t), 0, Math.PI * 2);
    ctx.fill();

    // ── Small ice shard chips scattered around impact ──────────────────────
    if (t < 0.6) {
      const chips = [
        { dx: 8, dy: -5 }, { dx: -6, dy: -8 }, { dx: 10, dy: 4 },
        { dx: -9, dy: 6 },  { dx: 4, dy: 10 },  { dx: -4, dy: -12 },
      ];
      for (const chip of chips) {
        const cx = x + chip.dx * out;
        const cy = y + chip.dy * out;
        ctx.globalAlpha = fade * 0.7;
        ctx.shadowBlur  = 6;
        this.drawIceStar(ctx, cx, cy, 2);
      }
    }

    ctx.restore();
  }

  // 4-pointed ice star / sparkle
  private drawIceStar(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
    ctx.fillStyle = "#e0f8ff";
    ctx.beginPath();
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      const outer = { x: x + Math.cos(a) * r,       y: y + Math.sin(a) * r };
      const inner = { x: x + Math.cos(a + Math.PI / 4) * r * 0.35, y: y + Math.sin(a + Math.PI / 4) * r * 0.35 };
      if (i === 0) ctx.moveTo(outer.x, outer.y); else ctx.lineTo(outer.x, outer.y);
      ctx.lineTo(inner.x, inner.y);
    }
    ctx.closePath();
    ctx.fill();
  }

  isDone(): boolean {
    return this.elapsed >= this.delayMs + TOTAL;
  }
}
